import { authFetch } from '@/lib/auth-client';
import { getDeviceId } from '@/lib/device-id';

type NotificationTrigger =
  | 'prop_lock'
  | 'prize_win'
  | 'draft_start'
  | 'game_start'
  | 'settlement'
  | 'prize_claim_reminder'
  | 'daily_pick'
  | 'daily_score'
  | 'picks_reminder';

type ScheduleTiming =
  | { scheduledAt: string; scheduledInDays?: never }
  | { scheduledAt?: never; scheduledInDays: number };

interface TriggerConfig {
  assetId: string;
  title: string;
  cta: string;
  priority: JestSDKNotificationPriority;
}

interface CustomContent {
  title?: string;
  cta?: string;
}

type ScheduleArgs = ScheduleTiming & {
  trigger: NotificationTrigger;
  entityId: string;
  entryPayload: JestSDKEntryPayload;
  content?: CustomContent;
};

interface MainEventNotificationSource {
  id: string;
  title?: string;
  sport?: string;
  status?: string;
  startTime?: string | Date;
  endTime?: string | Date | null;
  undercards?: Array<{
    id: string;
    title?: string;
    label?: string;
    lockTime?: string | Date;
    startTime?: string | Date;
    status?: string;
  }>;
}

interface PropBoostNotificationSource {
  id: string;
  // prop-boost events carry the display name in `name`; `title` kept as a
  // fallback for any other source shape.
  name?: string;
  title?: string;
  sport?: string;
  status?: string;
  entryDeadline?: string | Date;
  startTime?: string | Date;
  endTime?: string | Date;
}

// Jest Games Fund — hard constraints from the submission checklist.
const CTA_MAX_LENGTH = 25;
const BODY_MAX_LENGTH = 100;
// Basic-character whitelist: printable ASCII only. No emoji, no smart quotes,
// no characters that have surprised an iOS notification renderer in the past.
const BASIC_CHARS_RE = /^[\x20-\x7E]*$/;

// Window enforced by Jest's notification pipeline. Anything outside this is
// silently dropped, so we'd rather skip locally and log than push a junk
// schedule.
const MIN_SCHEDULE_DAYS = 1;
const MAX_SCHEDULE_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// daily_score is a short re-engagement burst AFTER an entered event settles —
// a daily "come see your score" CTA (Deyan/Steelhead's ask). It starts the day
// AFTER the one-shot `settlement` "results are in" so the two don't double up
// on day one, and runs a few days so a user who didn't reopen still gets nudged.
const DAILY_SCORE_START_DAY = MIN_SCHEDULE_DAYS + 1; // day 2
const DAILY_SCORE_END_DAY = MIN_SCHEDULE_DAYS + 3; // day 4

// Triggers that only make sense before/at their anchor — a reminder delivered
// after the lock/draft/tip-off has happened is worse than none, so we skip them
// when the anchor is inside the SDK's 1-day floor rather than pulling them
// forward. game_start joins this set: a "your game is starting" that lands a
// day after tip-off is noise.
const DEADLINE_TRIGGERS = new Set<NotificationTrigger>([
  'prop_lock',
  'draft_start',
  'game_start',
  // picks_reminder slots target specific game days — pulled forward to the
  // 1-day floor they'd land after the games they promote.
  'picks_reminder',
]);

// Fuzzy scheduling — spread notifications across a window so we don't ping
// every user at the exact same instant. Different jitter per trigger because
// "draft_start" matters more on-time than "see your results".
const JITTER_BUCKETS_MS: Record<NotificationTrigger, { before: number; after: number }> = {
  // Fire BEFORE the actual lock so the user has a runway, with a few minutes
  // of spread so concurrent users don't slam the app at once.
  prop_lock: { before: 30 * 60 * 1000, after: 5 * 60 * 1000 },
  // Land slightly AFTER game time so the draft window is genuinely open.
  draft_start: { before: 0, after: 2 * 60 * 1000 },
  // Land slightly AFTER tip-off so "your game is starting" is true when it
  // arrives, with a touch of spread so concurrent entrants don't all ping at once.
  game_start: { before: 0, after: 2 * 60 * 1000 },
  // Settlement / prize_win / prize_claim_reminder are not time-critical;
  // spread them across ~30 min so the inbox doesn't spike.
  prize_win: { before: 0, after: 30 * 60 * 1000 },
  settlement: { before: 0, after: 30 * 60 * 1000 },
  prize_claim_reminder: { before: 0, after: 60 * 60 * 1000 },
  // Re-engagement nudge — spread across an hour, no need to be punctual.
  daily_pick: { before: 0, after: 60 * 60 * 1000 },
  // Post-settlement "see your score" nudge — also not time-critical.
  daily_score: { before: 0, after: 60 * 60 * 1000 },
  // Campaign nudge aimed at a morning/afternoon slot, not an exact kickoff.
  // Spread BEFORE the slot only: slots are validated against the round's end,
  // and positive jitter could push one past the last game.
  picks_reminder: { before: 30 * 60 * 1000, after: 0 },
};

const PASS_STATUS_ASSETS = new Set([
  'prop_lock_banner',
  'prize_won_banner',
  'draft_start_banner',
  'settled_banner',
  'daily_pick_banner',
  'game_start_banner',
  'daily_score_banner',
]);

const NOTIFICATION_TRIGGER_MAP: Record<NotificationTrigger, TriggerConfig> = {
  prop_lock: {
    assetId: 'prop_lock_banner',
    title: 'Lock in your picks',
    cta: 'Make picks',
    priority: 'high',
  },
  prize_win: {
    assetId: 'prize_won_banner',
    title: 'Claim your prize',
    cta: 'Claim prize',
    priority: 'high',
  },
  draft_start: {
    assetId: 'draft_start_banner',
    title: 'Join the draft',
    cta: 'Join draft',
    priority: 'critical',
  },
  game_start: {
    assetId: 'game_start_banner',
    title: 'Your game is starting',
    cta: 'Watch live',
    priority: 'high',
  },
  settlement: {
    assetId: 'settled_banner',
    title: 'See your results',
    cta: 'View results',
    priority: 'medium',
  },
  prize_claim_reminder: {
    assetId: 'prize_won_banner',
    title: 'Claim your prize',
    cta: 'Claim prize',
    priority: 'high',
  },
  daily_pick: {
    assetId: 'daily_pick_banner',
    title: 'Your daily pick is ready',
    cta: 'Make your pick',
    priority: 'medium',
  },
  daily_score: {
    assetId: 'daily_score_banner',
    title: 'See your score',
    cta: 'See score',
    priority: 'medium',
  },
  picks_reminder: {
    // Reuses the approved prop_lock banner — this campaign ships without a
    // dedicated asset.
    assetId: 'prop_lock_banner',
    title: 'Get your picks in before the game starts!',
    cta: 'Make picks',
    // High so it outranks our own daily_pick (medium) in Jest's
    // one-notification-per-player-per-day selection.
    priority: 'high',
  },
};

// Called immediately after the SDK reports the player has finished
// registration (registered === true). The Jest Games Fund rubric requires
// notifications be scheduled "early — waiting for player registration", so
// we kick off boot scheduling the instant that flips.
export async function scheduleAfterRegistration(sdk: JestSDKApi | null): Promise<void> {
  if (!sdk?.notifications?.scheduleNotification) return;
  await scheduleBootNotifications(sdk);
}

export async function scheduleBootNotifications(sdk: JestSDKApi | null): Promise<void> {
  // The boot pass runs fire-and-forget, so every exit must say why — these
  // logs are the only way to see in a production build (simulator included)
  // which gate stopped scheduling.
  if (!sdk?.notifications?.scheduleNotification) {
    console.info('[notifications] boot: skipped — SDK notifications API unavailable');
    return;
  }

  try {
    // Gate boot scheduling on the player being a registered Jest user — the
    // platform only delivers notifications to registered users. The SDK's
    // getPlayer() returns synchronously (not a Promise), so no promise
    // chaining — `await` tolerates both, `.catch()` does not.
    let player: JestSDKPlayer | null = null;
    try {
      player = await sdk.getPlayer();
    } catch {
      player = null;
    }
    if (player && player.registered === false) {
      console.info('[notifications] boot: skipped — guest player');
      return;
    }

    // Re-engagement nudge — isolated so a failure here can't abort the
    // contest scheduling below, and independent of the API payload so it
    // still schedules if /api/v1/me is unavailable.
    try {
      await scheduleDailyPick(sdk);
    } catch (error) {
      console.warn('[notifications] boot: daily_pick failed (continuing):', error);
    }

    // Knockout picks campaigns — isolated like daily_pick so a failure here
    // can't abort the contest scheduling below.
    try {
      await schedulePicksCampaigns(sdk);
    } catch (error) {
      console.warn('[notifications] boot: picks_reminder failed (continuing):', error);
    }

    const response = await authFetch('/api/v1/me');
    if (!response.ok) {
      console.info(`[notifications] boot: /api/v1/me returned ${response.status} — contest scheduling skipped`);
      return;
    }

    const payload = await response.json();
    await scheduleNotificationPayload(sdk, payload, 'boot');
    console.info('[notifications] boot: scheduling pass complete');
  } catch (error) {
    console.warn('[notifications] boot scheduling skipped:', error);
  }
}

// Daily re-engagement: queue a rolling series of "come back for today's pick"
// reminders across the full deliverable window (Jest's 1-day floor means the
// earliest is tomorrow; the 7-day ceiling is the furthest it'll accept). A
// single boot used to schedule only tomorrow's nudge, so a user who didn't
// reopen the app got no further reminders. Now each day in the window gets its
// own notification, keyed on that day's UTC calendar date — so repeat boots the
// same day dedupe per-day, and as the window rolls forward each new day's slot
// is added while past ones have already fired.
async function scheduleDailyPick(sdk: JestSDKApi): Promise<void> {
  let scheduled = 0;
  for (let day = MIN_SCHEDULE_DAYS; day <= MAX_SCHEDULE_DAYS; day++) {
    // The calendar date this notification will land on — stable dedupe key per
    // day so the rolling series doesn't double up across boots.
    const targetDay = new Date(Date.now() + day * MS_PER_DAY).toISOString().slice(0, 10);
    const outcome = await scheduleNotification(sdk, {
      trigger: 'daily_pick',
      entityId: `daily:${targetDay}`,
      scheduledInDays: day,
      entryPayload: { screen: 'daily_pick', route: '/' },
    });
    if (outcome === 'scheduled') scheduled++;
  }
  // Per-day 'failed' already warned inside scheduleNotification; dedupe skips
  // are intentionally silent. This line reports how many were newly queued.
  console.info(
    `[notifications] daily_pick: ${scheduled} new day(s) submitted across the ${MIN_SCHEDULE_DAYS}-${MAX_SCHEDULE_DAYS}d window`
  );
}

// Post-settlement "see your score" re-engagement burst. Seeds a short daily
// series the FIRST time we observe a given entered event as settled, then never
// re-seeds — otherwise every reopen would roll the target dates forward and nag
// the user about an old result forever. Each day gets its own per-date key so
// the burst dedupes cleanly across boots. Callers only invoke this for events
// the user actually entered and that have settled.
async function scheduleDailyScore(
  sdk: JestSDKApi,
  args: { eventId: string; label: string; route: string; extraPayload?: JestSDKEntryPayload }
): Promise<void> {
  // One-time seed guard, namespaced apart from the per-notification dedupe keys.
  const seedKey = `fantasyprime:notification:daily_score_seed:${args.eventId}`;
  if (hasScheduled(seedKey)) return;

  const label = sanitizeBasic(args.label) ?? 'your event';
  let scheduled = 0;
  // Track whether the series is actually in place. A day that newly schedules
  // OR already exists (dedupe) counts as "ensured"; only hard failures / no-api
  // don't. We claim the seed AFTER the loop and only when at least one day was
  // ensured, so a transient SDK error on the first attempt can't permanently
  // suppress the whole post-settlement burst — the next pass retries.
  let ensured = false;
  for (let day = DAILY_SCORE_START_DAY; day <= DAILY_SCORE_END_DAY; day++) {
    const targetDay = new Date(Date.now() + day * MS_PER_DAY).toISOString().slice(0, 10);
    const outcome = await scheduleNotification(sdk, {
      trigger: 'daily_score',
      entityId: `${args.eventId}:${targetDay}`,
      scheduledInDays: day,
      entryPayload: {
        ...(args.extraPayload ?? {}),
        eventId: args.eventId,
        screen: 'results',
        route: args.route,
      },
      content: {
        title: clampBody(`See how you scored in ${label}`),
        cta: clampCta('See score'),
      },
    });
    if (outcome === 'scheduled') scheduled++;
    if (outcome === 'scheduled' || outcome === 'skipped:dedupe') ensured = true;
  }
  // Per-day notifications already dedupe on their own keys, so even if a
  // concurrent boot/page call also runs this loop before the seed is claimed,
  // no duplicates go out — the seed is just an optimization to skip re-running.
  if (ensured) markScheduled(seedKey);
  console.info(`[notifications] daily_score: ${scheduled} day(s) seeded for ${args.eventId}`);
}

// Knockout-round picks campaigns: nudge every registered user (entered or not)
// toward a specific World Cup round's picks page. Each campaign targets one
// undercard by title and queues a set of slots:
//   - `fixedSlots`: exact instants (e.g. a noon-ET reminder on the round's
//     opening day).
//   - `dailyGameDaySlots`: when true, an additional 9am-ET slot on each day
//     that actually has a fixture, derived from the undercard's games so
//     off-days never get one.
// When two slots share a date, Jest's one-notification-per-player-per-day cap
// delivers whichever it ranks higher. Event/undercard ids differ per
// environment, so they're resolved from the API at boot rather than hardcoded.
interface PicksCampaign {
  key: string;
  sport: string;
  undercardTitle: string;
  dailySlotUtcHour: number;
  fixedSlots: readonly string[];
  dailyGameDaySlots: boolean;
  content?: { title?: string; cta?: string };
}

const PICKS_CAMPAIGNS: readonly PicksCampaign[] = [
  // Round of 32 (concluded 2026-07-04) — kept for reference; the liveness and
  // window guards make it a no-op once the round ends.
  {
    key: 'Round of 32',
    sport: 'SOCCER_WORLD_CUP',
    undercardTitle: 'Round of 32',
    dailySlotUtcHour: 13, // 9am ET (EDT)
    fixedSlots: ['2026-07-03T19:30:00.000Z'], // 3:30pm ET, 2026-07-03
    dailyGameDaySlots: true,
  },
  // Quarter-Finals opening-day reminder — a single noon-ET nudge on 2026-07-09
  // (round starts that evening). No game-day series: this is a "picks are open"
  // heads-up, not a per-fixture reminder.
  {
    key: 'Quarter-Finals',
    sport: 'SOCCER_WORLD_CUP',
    undercardTitle: 'Quarter-Finals',
    dailySlotUtcHour: 13,
    fixedSlots: ['2026-07-09T16:00:00.000Z'], // 12pm ET, 2026-07-09
    dailyGameDaySlots: false,
    content: {
      title: 'Quarterfinals start today! Make your picks - you can still win a share of $5000 in prizes!',
      cta: 'Make picks',
    },
  },
] as const;

// ET offset while the campaigns run (EDT, matches dailySlotUtcHour above).
const ET_UTC_OFFSET_MS = 4 * 60 * 60 * 1000;

async function schedulePicksCampaigns(sdk: JestSDKApi): Promise<void> {
  for (const campaign of PICKS_CAMPAIGNS) {
    try {
      await schedulePicksCampaign(sdk, campaign);
    } catch (error) {
      console.warn(`[notifications] picks_reminder: ${campaign.key} failed (continuing):`, error);
    }
  }
}

async function schedulePicksCampaign(sdk: JestSDKApi, campaign: PicksCampaign): Promise<void> {
  // /api/main-events (unsliced, server-side sport filter) rather than
  // /api/dashboard, whose events array is capped at 6 and could drop the
  // knockout event entirely.
  const eventsRes = await authFetch(`/api/main-events?sport=${campaign.sport}`);
  if (!eventsRes.ok) {
    console.info(`[notifications] picks_reminder(${campaign.key}): /api/main-events returned ${eventsRes.status} — skipped`);
    return;
  }
  const eventsPayload = await eventsRes.json();
  const candidateIds = (Array.isArray(eventsPayload) ? eventsPayload : [])
    .filter(
      (event): event is Record<string, unknown> =>
        isRecord(event) && readString(event.status) !== 'COMPLETED'
    )
    .map((event) => readString(event.id))
    .filter((id): id is string => !!id);
  if (candidateIds.length === 0) {
    console.info(`[notifications] picks_reminder(${campaign.key}): no active World Cup event — skipped`);
    return;
  }

  // Several WC events can be live at once (Group Stage is also
  // SOCCER_WORLD_CUP and can linger as LOCKED until settled) — probe each
  // until one carries a LIVE target undercard. A finished round on one
  // candidate doesn't rule out a live one on another, so keep probing.
  let eventId: string | undefined;
  let undercardId: string | undefined;
  let endMs: number | null = null;
  for (const candidateId of candidateIds) {
    const detailRes = await authFetch(`/api/main-events/${candidateId}`);
    if (!detailRes.ok) continue;
    const detail = await detailRes.json();
    const undercards = isRecord(detail) && Array.isArray(detail.undercards) ? detail.undercards : [];
    const match = undercards.find(
      (undercard): undercard is Record<string, unknown> =>
        isRecord(undercard) && readString(undercard.title) === campaign.undercardTitle
    );
    const matchId = match ? readString(match.id) : undefined;
    if (!match || !matchId) continue;
    const endIso = readDate(match.endTime);
    const matchEndMs = endIso ? new Date(endIso).getTime() : null;
    if (readString(match.status) === 'COMPLETED' || (matchEndMs != null && matchEndMs <= Date.now())) {
      continue;
    }
    eventId = candidateId;
    undercardId = matchId;
    endMs = matchEndMs;
    break;
  }
  if (!eventId || !undercardId) {
    console.info(`[notifications] picks_reminder(${campaign.key}): no live undercard — skipped`);
    return;
  }

  const slots = new Set<string>(campaign.fixedSlots);
  // 9am ET slots only on days that actually have fixtures — derived from the
  // undercard's games rather than its start/end span, which can include
  // off-days. Kickoffs are bucketed by ET calendar day to match the ET slot
  // hour (a 9:30pm ET kickoff is already the next day in UTC). If the games
  // fetch fails, fall back to the fixed slots alone rather than guessing days.
  if (campaign.dailyGameDaySlots) {
    const gamesRes = await authFetch(`/api/main-events/${eventId}/undercards/${undercardId}`);
    if (gamesRes.ok) {
      const undercardDetail = await gamesRes.json();
      const games =
        isRecord(undercardDetail) && Array.isArray(undercardDetail.games) ? undercardDetail.games : [];
      for (const game of games) {
        if (!isRecord(game)) continue;
        const gameIso = readDate(game.gameTime);
        if (!gameIso) continue;
        const etDay = new Date(new Date(gameIso).getTime() - ET_UTC_OFFSET_MS).toISOString().slice(0, 10);
        slots.add(`${etDay}T${String(campaign.dailySlotUtcHour).padStart(2, '0')}:00:00.000Z`);
      }
    } else {
      console.info(
        `[notifications] picks_reminder(${campaign.key}): undercard games returned ${gamesRes.status} — fixed slots only`
      );
    }
  }

  const content = campaign.content
    ? {
        ...(campaign.content.title ? { title: clampBody(campaign.content.title) } : {}),
        ...(campaign.content.cta ? { cta: clampCta(campaign.content.cta) } : {}),
      }
    : undefined;

  const route = `/events/main/${eventId}/undercards/${undercardId}`;
  let scheduled = 0;
  for (const slotIso of [...slots].sort()) {
    // A nudge landing after the round's last game promotes nothing — drop it.
    // (Slots in the past or beyond the 7-day window are dropped downstream by
    // clampToWindow.)
    if (endMs != null && new Date(slotIso).getTime() > endMs) continue;
    const outcome = await scheduleNotification(sdk, {
      trigger: 'picks_reminder',
      // Keyed on undercard + date+hour so slots dedupe independently across
      // boots; the undercard id keeps campaigns from colliding.
      entityId: `${undercardId}:${slotIso.slice(0, 13)}`,
      scheduledAt: slotIso,
      entryPayload: {
        eventId,
        contestId: eventId,
        undercardId,
        screen: 'prop_locks',
        route,
      },
      ...(content ? { content } : {}),
    });
    if (outcome === 'scheduled') scheduled++;
  }
  console.info(`[notifications] picks_reminder(${campaign.key}): ${scheduled} slot(s) submitted`);
}

async function scheduleNotificationPayload(
  sdk: JestSDKApi | null,
  payload: unknown,
  source = 'api'
): Promise<void> {
  if (!sdk?.notifications?.scheduleNotification || !isRecord(payload)) return;

  const candidates = [
    payload.notifications,
    payload.notificationTriggers,
    payload.scheduledNotifications,
    isRecord(payload.user) ? payload.user.notifications : null,
  ];

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    for (const item of candidate) {
      if (!isRecord(item)) continue;
      await scheduleLooseTrigger(sdk, item, source);
    }
  }

  const contestCandidates = [
    payload.contest,
    payload.currentContest,
    payload.nextContest,
    ...(Array.isArray(payload.contests) ? payload.contests : []),
  ];

  for (const contest of contestCandidates) {
    if (isRecord(contest)) {
      await scheduleContestNotifications(sdk, contest, source);
    }
  }

  // Pending claims surfaced from /api/v1/me — wire them into prize_claim_reminder
  // so users with unclaimed prizes get a nudge tomorrow. Key on the event id
  // (when present) so this matches the page path's prize_claim_reminder
  // (scheduleMainEventNotifications also uses event.id) and the two dedupe
  // instead of double-firing; multiple unclaimed prizes in one event collapse
  // to a single reminder. Falls back to the claim id for payload sources that
  // don't carry mainEventId.
  if (Array.isArray(payload.pendingClaims)) {
    for (const claim of payload.pendingClaims) {
      if (!isRecord(claim)) continue;
      const claimId = readString(claim.id) ?? readString(claim.prizeId);
      const eventId = readString(claim.mainEventId);
      if (!claimId && !eventId) continue;
      await scheduleNotification(sdk, {
        trigger: 'prize_claim_reminder',
        entityId: eventId ?? `claim:${claimId}`,
        scheduledInDays: 1,
        entryPayload: {
          screen: 'prize',
          route: eventId ? `/events/main/${eventId}` : '/me/prizes',
          claimId,
        },
      });
    }
  }
}

export async function scheduleMainEventNotifications(
  sdk: JestSDKApi | null,
  event: MainEventNotificationSource,
  options: {
    hasEntry?: boolean;
    wonPrize?: boolean;
    unclaimedPrize?: boolean;
    // Per-undercard "has the user locked picks?" — used to skip prop_lock
    // reminders for undercards they've already completed.
    picksByUndercard?: Record<string, boolean>;
  } = {}
): Promise<void> {
  if (!sdk?.notifications?.scheduleNotification) return;

  const eventLabel = sanitizeBasic(event.title) ?? 'your event';
  const sportLabel = sanitizeBasic(event.sport);
  const eventPayload: JestSDKEntryPayload = {
    eventId: event.id,
    contestId: event.id,
    screen: 'main_event',
    route: `/events/main/${event.id}`,
  };

  // "Join the draft" is a come-in nudge — only for users who haven't entered.
  // Once they're in, forget any prior schedule so a refresh doesn't re-add it.
  if (event.startTime) {
    if (options.hasEntry) {
      forgetScheduled('draft_start', event.id);
    } else {
      await scheduleNotification(sdk, {
        trigger: 'draft_start',
        entityId: event.id,
        scheduledAt: toIso(event.startTime),
        entryPayload: eventPayload,
        content: {
          title: clampBody(`${eventLabel} is starting`),
          cta: clampCta('Join now'),
        },
      });
    }
  }

  if (options.hasEntry) {
    for (const undercard of event.undercards ?? []) {
      const undercardLabel = sanitizeBasic(undercard.title ?? undercard.label) ?? eventLabel;
      // Progress-based: if the user has already locked picks for this
      // undercard, skip the prop_lock reminder (and cancel any already
      // scheduled by removing the dedupe so re-runs don't re-add it). Instead,
      // remind them when the game itself tips off (game_start).
      const alreadyPicked = options.picksByUndercard?.[undercard.id] === true;
      if (alreadyPicked) {
        forgetScheduled('prop_lock', undercard.id);
        if (undercard.startTime) {
          await scheduleNotification(sdk, {
            trigger: 'game_start',
            entityId: undercard.id,
            scheduledAt: toIso(undercard.startTime),
            entryPayload: {
              ...eventPayload,
              undercardId: undercard.id,
              route: `/events/main/${event.id}/undercards/${undercard.id}`,
            },
            content: {
              title: clampBody(`${undercardLabel} is starting`),
              cta: clampCta('Watch live'),
            },
          });
        }
        continue;
      }
      if (!undercard.lockTime) continue;
      await scheduleNotification(sdk, {
        trigger: 'prop_lock',
        entityId: undercard.id,
        scheduledAt: toIso(undercard.lockTime),
        entryPayload: {
          ...eventPayload,
          undercardId: undercard.id,
          screen: 'prop_locks',
          route: `/events/main/${event.id}/undercards/${undercard.id}`,
        },
        content: {
          title: clampBody(`Picks lock soon: ${undercardLabel}`),
          cta: clampCta('Make picks'),
        },
      });
    }
  }

  if (options.wonPrize) {
    await scheduleNotification(sdk, {
      trigger: 'prize_win',
      entityId: event.id,
      scheduledInDays: 1,
      entryPayload: { ...eventPayload, screen: 'prize', route: `/events/main/${event.id}` },
      content: {
        title: clampBody(`You won a prize in ${eventLabel}`),
      },
    });
  }

  // Only entrants care about results — don't push "results are in" to someone
  // who merely opened a finished event page without entering.
  if (options.hasEntry && event.status === 'COMPLETED') {
    await scheduleNotification(sdk, {
      trigger: 'settlement',
      entityId: event.id,
      scheduledInDays: 1,
      entryPayload: { ...eventPayload, screen: 'results', route: `/events/main/${event.id}` },
      content: {
        title: clampBody(
          sportLabel ? `${eventLabel} results are in (${sportLabel})` : `${eventLabel} results are in`
        ),
      },
    });
    // Daily "see your score" re-engagement burst following the one-shot above.
    await scheduleDailyScore(sdk, {
      eventId: event.id,
      label: eventLabel,
      route: `/events/main/${event.id}`,
      extraPayload: { contestId: event.id },
    });
  }

  if (options.unclaimedPrize) {
    await scheduleNotification(sdk, {
      trigger: 'prize_claim_reminder',
      entityId: event.id,
      scheduledInDays: 1,
      entryPayload: { ...eventPayload, screen: 'prize', route: `/events/main/${event.id}` },
      content: {
        title: clampBody(`Don't forget your ${eventLabel} prize`),
      },
    });
  }
}

export async function scheduleUndercardNotifications(
  sdk: JestSDKApi | null,
  undercard: {
    id: string;
    mainEventId: string;
    title?: string;
    label?: string;
    lockTime?: string | Date;
    startTime?: string | Date;
    status?: string;
  },
  options: { hasEntry?: boolean; hasMadePicks?: boolean } = {}
): Promise<void> {
  // Don't require lockTime here: a locked-picks entrant still needs the
  // game_start (startTime) reminder, and a COMPLETED undercard still needs its
  // settlement notice — neither depends on a lock time. Only the prop_lock
  // reminder itself is gated on lockTime below.
  if (!sdk?.notifications?.scheduleNotification || !options.hasEntry) return;

  const undercardLabel = sanitizeBasic(undercard.title ?? undercard.label) ?? 'your undercard';

  // Progress: if picks are already made, don't pester with prop_lock. Also
  // clear the dedupe so that a previously-scheduled reminder is logically
  // cancelled on the client (we can't pull a notification back from Jest, but
  // we mark it as not-our-problem-anymore so a refresh doesn't re-add it).
  // Instead, remind them when the game itself tips off (game_start).
  if (options.hasMadePicks) {
    forgetScheduled('prop_lock', undercard.id);
    if (undercard.startTime) {
      await scheduleNotification(sdk, {
        trigger: 'game_start',
        entityId: undercard.id,
        scheduledAt: toIso(undercard.startTime),
        entryPayload: {
          eventId: undercard.mainEventId,
          contestId: undercard.mainEventId,
          undercardId: undercard.id,
          screen: 'main_event',
          route: `/events/main/${undercard.mainEventId}/undercards/${undercard.id}`,
        },
        content: {
          title: clampBody(`${undercardLabel} is starting`),
          cta: clampCta('Watch live'),
        },
      });
    }
  } else if (undercard.lockTime) {
    await scheduleNotification(sdk, {
      trigger: 'prop_lock',
      entityId: undercard.id,
      scheduledAt: toIso(undercard.lockTime),
      entryPayload: {
        eventId: undercard.mainEventId,
        contestId: undercard.mainEventId,
        undercardId: undercard.id,
        screen: 'prop_locks',
        route: `/events/main/${undercard.mainEventId}/undercards/${undercard.id}`,
      },
      content: {
        title: clampBody(`Picks lock soon: ${undercardLabel}`),
        cta: clampCta('Make picks'),
      },
    });
  }

  if (undercard.status === 'COMPLETED') {
    // Settlement is a contest-level "results are in" — key it on the event id
    // (the same key scheduleMainEventNotifications uses) so an entrant who
    // opens the main event and one or more completed undercards gets a single
    // settlement notification per contest, not one per undercard.
    await scheduleNotification(sdk, {
      trigger: 'settlement',
      entityId: undercard.mainEventId,
      scheduledInDays: 1,
      entryPayload: {
        eventId: undercard.mainEventId,
        contestId: undercard.mainEventId,
        undercardId: undercard.id,
        screen: 'results',
        route: `/events/main/${undercard.mainEventId}/undercards/${undercard.id}`,
      },
      content: {
        title: clampBody(`${undercardLabel} is settled`),
      },
    });
    // NOTE: the "see your score" (daily_score) burst is intentionally NOT seeded
    // here. A single completed undercard doesn't mean the whole contest is
    // settled, and seeding it on the event id would lock the one-time seed and
    // start the burst prematurely. The event-level burst is seeded only once the
    // whole contest settles — by scheduleMainEventNotifications, the prop-boost
    // path, or the boot/contest path.
  }
}

export async function schedulePropBoostNotifications(
  sdk: JestSDKApi | null,
  event: PropBoostNotificationSource,
  options: { hasEntry?: boolean; hasMadePicks?: boolean; wonPrize?: boolean } = {}
): Promise<void> {
  if (!sdk?.notifications?.scheduleNotification) return;

  const eventLabel =
    sanitizeBasic(event.name) ?? sanitizeBasic(event.title) ?? 'your prop boost';
  const entryPayload: JestSDKEntryPayload = {
    eventId: event.id,
    contestId: event.id,
    screen: 'prop_boost',
    route: `/events/prop-boost/${event.id}`,
  };

  // "Join now" is for users who haven't entered yet; once in, forget any prior
  // schedule. Entrants who have locked picks get game_start at tip-off instead.
  if (event.startTime) {
    if (options.hasEntry) {
      forgetScheduled('draft_start', event.id);
    } else {
      await scheduleNotification(sdk, {
        trigger: 'draft_start',
        entityId: event.id,
        scheduledAt: toIso(event.startTime),
        entryPayload,
        content: {
          title: clampBody(`${eventLabel} is starting`),
          cta: clampCta('Join now'),
        },
      });
    }
  }

  if (options.hasEntry && event.entryDeadline) {
    if (options.hasMadePicks) {
      forgetScheduled('prop_lock', event.id);
    } else {
      await scheduleNotification(sdk, {
        trigger: 'prop_lock',
        entityId: event.id,
        scheduledAt: toIso(event.entryDeadline),
        entryPayload: { ...entryPayload, screen: 'prop_locks' },
        content: {
          title: clampBody(`Boost picks lock: ${eventLabel}`),
          cta: clampCta('Make picks'),
        },
      });
    }
  }

  // Game tip-off reminder for entrants who have locked their picks.
  if (options.hasEntry && options.hasMadePicks && event.startTime) {
    await scheduleNotification(sdk, {
      trigger: 'game_start',
      entityId: event.id,
      scheduledAt: toIso(event.startTime),
      entryPayload: { ...entryPayload, screen: 'prop_boost' },
      content: {
        title: clampBody(`${eventLabel} is starting`),
        cta: clampCta('Watch live'),
      },
    });
  }

  if (options.wonPrize) {
    await scheduleNotification(sdk, {
      trigger: 'prize_win',
      entityId: event.id,
      scheduledInDays: 1,
      entryPayload: { ...entryPayload, screen: 'prize' },
      content: {
        title: clampBody(`You won a prize in ${eventLabel}`),
      },
    });
  }

  if (options.hasEntry && event.status === 'COMPLETED') {
    await scheduleNotification(sdk, {
      trigger: 'settlement',
      entityId: event.id,
      scheduledInDays: 1,
      entryPayload: { ...entryPayload, screen: 'results' },
      content: {
        title: clampBody(`${eventLabel} results are in`),
      },
    });
    await scheduleDailyScore(sdk, {
      eventId: event.id,
      label: eventLabel,
      route: `/events/prop-boost/${event.id}`,
      extraPayload: { contestId: event.id },
    });
  }
}

export function getRouteFromEntryPayload(payload: JestSDKEntryPayload | null | undefined): string | null {
  if (!payload) return null;

  if (typeof payload.route === 'string' && payload.route.startsWith('/')) {
    return payload.route;
  }

  if (typeof payload.eventId === 'string' && typeof payload.undercardId === 'string') {
    return `/events/main/${payload.eventId}/undercards/${payload.undercardId}`;
  }

  if (typeof payload.eventId === 'string') {
    return payload.screen === 'prop_boost'
      ? `/events/prop-boost/${payload.eventId}`
      : `/events/main/${payload.eventId}`;
  }

  if (typeof payload.contestId === 'string') {
    return `/contests/${payload.contestId}`;
  }

  return null;
}

async function scheduleContestNotifications(
  sdk: JestSDKApi,
  contest: Record<string, unknown>,
  source: string
): Promise<void> {
  const id = readString(contest.id) ?? readString(contest.contestId) ?? source;
  const route = readString(contest.route) ?? readString(contest.deepLink) ?? `/contests/${id}`;
  const label = sanitizeBasic(readString(contest.title) ?? readString(contest.name)) ?? 'your contest';
  const entryPayload: JestSDKEntryPayload = {
    contestId: id,
    eventId: readString(contest.eventId),
    undercardId: readString(contest.undercardId),
    screen: readString(contest.screen) ?? 'contest',
    route,
  };

  // prop_lock — keyed on the undercard id so this boot-path schedule and the
  // page path (scheduleMainEventNotifications, also undercard.id) dedupe and
  // cancel against each other. The backend sends still-pickable, future-lock
  // undercards in `contest.undercards`. Older/other payload sources without
  // that array fall back to a single contest-level lock keyed on the bare id.
  const undercards = Array.isArray(contest.undercards) ? contest.undercards : [];
  if (undercards.length > 0) {
    for (const raw of undercards) {
      if (!isRecord(raw)) continue;
      const undercardId = readString(raw.id);
      const lockAt = readDate(raw.lockTime);
      if (!undercardId) continue;
      // Prefer the undercard's own name for the body — these notifications deep
      // link to a specific undercard, so the contest-wide label would read as a
      // generic "your contest is starting". Fall back to the contest label.
      const ucLabel =
        sanitizeBasic(readString(raw.title) ?? readString(raw.name) ?? readString(raw.label)) ?? label;
      if (readBoolean(raw.hasMadePicks)) {
        forgetScheduled('prop_lock', undercardId);
        // Picks locked — remind them when the game tips off. Keyed on the
        // undercard id so it dedupes with the page path's game_start.
        const gameStartAt = readDate(raw.startTime) ?? readDate(raw.gameStartAt);
        if (gameStartAt) {
          await scheduleNotification(sdk, {
            trigger: 'game_start',
            entityId: undercardId,
            scheduledAt: gameStartAt,
            entryPayload: {
              ...entryPayload,
              undercardId,
              screen: 'main_event',
              route: `/events/main/${id}/undercards/${undercardId}`,
            },
            content: {
              title: clampBody(`${ucLabel} is starting`),
              cta: clampCta('Watch live'),
            },
          });
        }
        continue;
      }
      if (!lockAt) continue;
      await scheduleNotification(sdk, {
        trigger: 'prop_lock',
        entityId: undercardId,
        scheduledAt: lockAt,
        entryPayload: {
          ...entryPayload,
          undercardId,
          screen: 'prop_locks',
          route: `/events/main/${id}/undercards/${undercardId}`,
        },
        content: {
          title: clampBody(`Picks lock soon: ${ucLabel}`),
          cta: clampCta('Make picks'),
        },
      });
    }
  } else {
    const hasMadePicks = readBoolean(contest.hasMadePicks) || readBoolean(contest.isLocked);
    const propLockAt = readDate(contest.scheduledAt) ?? readDate(contest.propLockAt) ?? readDate(contest.lockTime) ?? readDate(contest.entryDeadline);
    if (propLockAt) {
      if (hasMadePicks) {
        forgetScheduled('prop_lock', id);
      } else {
        await scheduleNotification(sdk, {
          trigger: 'prop_lock',
          entityId: id,
          scheduledAt: propLockAt,
          entryPayload,
          content: {
            title: clampBody(`Picks lock soon: ${label}`),
            cta: clampCta('Make picks'),
          },
        });
      }
    }

    // Contest-level game_start (no per-undercard breakdown available): if the
    // user has locked picks and we know a start time, remind them at tip-off.
    const gameStartAt = readDate(contest.gameStartAt) ?? readDate(contest.startTime);
    if (hasMadePicks && gameStartAt) {
      await scheduleNotification(sdk, {
        trigger: 'game_start',
        entityId: id,
        scheduledAt: gameStartAt,
        entryPayload: { ...entryPayload, screen: 'contest' },
        content: {
          title: clampBody(`${label} is starting`),
          cta: clampCta('Watch live'),
        },
      });
    }
  }

  // "Join the draft" is a come-in nudge — skip it for users who've already
  // joined, and forget any prior schedule so a refresh doesn't re-add it.
  const hasJoined =
    readBoolean(contest.hasEntry) || readBoolean(contest.hasJoined) || readBoolean(contest.entered);
  const draftStartAt = readDate(contest.draftStartAt) ?? readDate(contest.startTime);
  if (draftStartAt) {
    if (hasJoined) {
      forgetScheduled('draft_start', id);
    } else {
      await scheduleNotification(sdk, {
        trigger: 'draft_start',
        entityId: id,
        scheduledAt: draftStartAt,
        entryPayload: { ...entryPayload, screen: 'draft' },
        content: {
          title: clampBody(`${label} draft is open`),
          cta: clampCta('Join draft'),
        },
      });
    }
  }

  if (readBoolean(contest.wonPrize) || readBoolean(contest.hasPrizeToClaim)) {
    await scheduleNotification(sdk, {
      trigger: 'prize_win',
      entityId: id,
      scheduledInDays: readNumber(contest.prizeScheduledInDays) ?? 1,
      entryPayload: { ...entryPayload, screen: 'prize' },
      content: {
        title: clampBody(`You won a prize in ${label}`),
      },
    });
  }

  if (readBoolean(contest.isSettled) || readString(contest.status) === 'COMPLETED') {
    await scheduleNotification(sdk, {
      trigger: 'settlement',
      entityId: id,
      scheduledInDays: readNumber(contest.settlementScheduledInDays) ?? 1,
      entryPayload: { ...entryPayload, screen: 'results' },
      content: {
        title: clampBody(`${label} results are in`),
      },
    });
    // "See your score" burst only for users who actually entered. If the
    // payload omits the entry flags this stays off on the boot path; the page
    // path seeds it when the entrant opens the settled event.
    if (hasJoined) {
      await scheduleDailyScore(sdk, {
        eventId: id,
        label,
        route,
        extraPayload: { contestId: id, eventId: readString(contest.eventId) },
      });
    }
  }
}

async function scheduleLooseTrigger(
  sdk: JestSDKApi,
  item: Record<string, unknown>,
  source: string
): Promise<void> {
  const trigger = normalizeTrigger(readString(item.trigger) ?? readString(item.type));
  if (!trigger) return;

  const scheduledAt = readDate(item.scheduledAt);
  const scheduledInDays = readNumber(item.scheduledInDays);
  if (!scheduledAt && scheduledInDays == null) return;

  const entityId = readString(item.entityId) ?? readString(item.contestId) ?? readString(item.eventId) ?? source;
  const route = readString(item.route) ?? readString(item.deepLink);
  const customTitle = sanitizeBasic(readString(item.title));
  const customCta = sanitizeBasic(readString(item.cta));

  await scheduleNotification(sdk, {
    trigger,
    entityId,
    ...(scheduledAt ? { scheduledAt } : { scheduledInDays: scheduledInDays ?? 0 }),
    entryPayload: {
      contestId: readString(item.contestId),
      eventId: readString(item.eventId),
      undercardId: readString(item.undercardId),
      screen: readString(item.screen) ?? trigger,
      ...(route ? { route } : {}),
    },
    content: {
      ...(customTitle ? { title: clampBody(customTitle) } : {}),
      ...(customCta ? { cta: clampCta(customCta) } : {}),
    },
  });
}

// Outcome of a schedule attempt, so callers that report status (e.g. the
// daily_pick boot log) reflect what actually happened — SDK failures are
// swallowed here for batch isolation and skips return early, so `await`
// completing says nothing by itself.
type ScheduleOutcome = 'scheduled' | 'failed' | 'skipped:window' | 'skipped:dedupe' | 'skipped:no-api';

async function scheduleNotification(sdk: JestSDKApi, args: ScheduleArgs): Promise<ScheduleOutcome> {
  const baseConfig = NOTIFICATION_TRIGGER_MAP[args.trigger];
  const finalConfig: TriggerConfig = {
    ...baseConfig,
    title: args.content?.title ?? baseConfig.title,
    cta: args.content?.cta ?? baseConfig.cta,
  };
  assertValidConfig(finalConfig);

  // Resolve absolute timestamp, apply 1–7 day window, then jitter.
  // Narrow via value, not `in`, since the discriminated union uses
  // `scheduledAt?: never` on the other branch and `in` doesn't always
  // narrow that to undefined.
  const explicitScheduledAt = (args as { scheduledAt?: string }).scheduledAt;
  const rawIso = explicitScheduledAt
    ? explicitScheduledAt
    : new Date(Date.now() + (args as { scheduledInDays: number }).scheduledInDays * MS_PER_DAY).toISOString();

  // prop_lock / draft_start only make sense before their anchor; if the anchor
  // is inside the SDK's 1-day floor we skip rather than fire a stale reminder
  // after the deadline. Time-insensitive triggers still nudge at the floor.
  const windowed = clampToWindow(rawIso, !DEADLINE_TRIGGERS.has(args.trigger));
  if (!windowed) {
    if (import.meta.env?.DEV) {
      console.debug(
        `[notifications] skipped ${args.trigger}:${args.entityId} — outside 1-7d window (${rawIso})`
      );
    }
    return 'skipped:window';
  }

  const fuzzedIso = applyJitter(args.trigger, args.entityId, windowed);

  // Bail before marking if the SDK can't actually deliver — otherwise the
  // dedupe key would be stored for a notification that never went out,
  // permanently suppressing it. (No optional chaining past this point.)
  if (!sdk.notifications?.scheduleNotification) return 'skipped:no-api';

  const dedupeKey = makeDedupeKey(args.trigger, args.entityId);
  if (hasScheduled(dedupeKey)) return 'skipped:dedupe';

  // Claim the dedupe slot *before* the await: boot and page scheduling run
  // concurrently (both fire-and-forget), so without this both could pass the
  // hasScheduled check for the same trigger+entity and double-send. Roll back
  // if the SDK call fails so a transient error doesn't suppress it forever.
  markScheduled(dedupeKey);
  try {
    await sdk.notifications.scheduleNotification({
      // SDK schema (jestsdk.js 26.06.10): `body`, `ctaText`, and `identifier`
      // are required; `assetReference` is the asset id as a plain string; and
      // `scheduledAt` must be a Date INSTANCE (validated with instanceof Date —
      // an ISO string fails the whole variant union). Our config's `title` is
      // the notification message, so it maps to `body`.
      body: finalConfig.title,
      ctaText: finalConfig.cta,
      priority: finalConfig.priority,
      assetReference: finalConfig.assetId,
      // Stable per logical notification (trigger + entity). The SDK uses it
      // for reschedule/unschedule, so re-sending the same identifier updates
      // the existing schedule instead of duplicating it.
      identifier: dedupeKey,
      entryPayload: args.entryPayload,
      scheduledAt: new Date(fuzzedIso),
    });
    return 'scheduled';
  } catch (err) {
    // Roll back the dedupe claim so this one retries next pass. Don't rethrow:
    // notifications are scheduled in batches (draft_start, per-undercard
    // prop_locks, settlement…) and one SDK failure shouldn't abort its
    // siblings — each is independent.
    forgetScheduled(args.trigger, args.entityId);
    // Always-on warn (not DEV-gated): a schedule failure in a production
    // build was previously invisible, which made the simulator impossible
    // to diagnose from logs.
    console.warn(
      `[notifications] schedule failed ${args.trigger}:${args.entityId}`,
      err
    );
    return 'failed';
  }
}

function assertValidConfig(config: TriggerConfig): void {
  if (!PASS_STATUS_ASSETS.has(config.assetId)) {
    throw new Error(`Notification asset is not pass-listed: ${config.assetId}`);
  }
  if (config.cta.length > CTA_MAX_LENGTH) {
    throw new Error(`Notification CTA exceeds ${CTA_MAX_LENGTH} chars: ${config.cta}`);
  }
  if (config.title.length > BODY_MAX_LENGTH) {
    throw new Error(`Notification title exceeds ${BODY_MAX_LENGTH} chars: ${config.title}`);
  }
  if (!BASIC_CHARS_RE.test(config.title)) {
    throw new Error(`Notification title uses non-basic characters: ${config.title}`);
  }
  if (!BASIC_CHARS_RE.test(config.cta)) {
    throw new Error(`Notification CTA uses non-basic characters: ${config.cta}`);
  }
}

// Title/body clamp — caller passes a content string, we strip non-basic chars
// then truncate at the body limit with an ellipsis-aware boundary.
function clampBody(value: string): string {
  const sanitized = sanitizeBasic(value) ?? '';
  if (sanitized.length <= BODY_MAX_LENGTH) return sanitized;
  return `${sanitized.slice(0, BODY_MAX_LENGTH - 1).trimEnd()}.`;
}

function clampCta(value: string): string {
  const sanitized = sanitizeBasic(value) ?? '';
  if (sanitized.length <= CTA_MAX_LENGTH) return sanitized;
  return sanitized.slice(0, CTA_MAX_LENGTH).trimEnd();
}

// Replace smart quotes / dashes / non-breaking spaces with ASCII equivalents
// then drop anything still outside printable ASCII. Returns undefined when
// the input is empty / nothing usable survives, so the caller can fall back
// to the default template.
function sanitizeBasic(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/[…]/g, '...')
    .replace(/ /g, ' ')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.length ? normalized : undefined;
}

// 1–7 day window clamp. Returns the ISO string to use, or null when the source
// time is past, too far out, or — for a deadline-bound trigger — too soon to
// remind before it passes.
//
// `pullForwardWhenSoon` decides what to do when the anchor is < 1 day out (Jest
// can't deliver sooner than MIN_SCHEDULE_DAYS): non-deadline reminders
// (settlement / prize) still nudge at the floor, but deadline-bound ones
// (prop_lock / draft_start) would land AFTER the event if pulled forward — a
// "picks lock soon" that arrives a day after the lock — so we skip them.
function clampToWindow(iso: string, pullForwardWhenSoon: boolean): string | null {
  const now = Date.now();
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;

  const earliest = now + MIN_SCHEDULE_DAYS * MS_PER_DAY;
  const latest = now + MAX_SCHEDULE_DAYS * MS_PER_DAY;

  if (t < now) return null; // already passed
  if (t > latest) return null; // too far out — Jest would reject it
  if (t < earliest) {
    return pullForwardWhenSoon ? new Date(earliest).toISOString() : null;
  }
  return new Date(t).toISOString();
}

// Per-trigger jitter. Pulls fire times slightly before/after the anchor so
// concurrent users don't all get pinged at the same instant. The seed mixes
// the per-device id so different devices get different offsets (the actual
// spread), plus trigger + entityId + anchor so the value is stable across
// reboots on a given device — repeated boots schedule the same instant and
// don't drift.
function applyJitter(
  trigger: NotificationTrigger,
  entityId: string,
  iso: string
): string {
  const bucket = JITTER_BUCKETS_MS[trigger];
  const t = new Date(iso).getTime();
  if (!bucket || !Number.isFinite(t)) return iso;

  const seed = hashString(`${getDeviceId()}:${trigger}:${entityId}:${iso}`);
  const span = bucket.before + bucket.after;
  const offset = span > 0 ? (seed % span) - bucket.before : 0;

  const fuzzed = t + offset;
  // Re-clamp after jitter. Lower bound: snap up if pushed below the 1-day
  // floor. Upper bound: the 7-day window edge — except prop_lock, whose
  // reminder is useless once the lock passes, so its ceiling is the lock
  // anchor itself (its bucket has a few minutes of positive jitter for spread,
  // which must never land "make picks" after the lock). draft_start keeps the
  // window ceiling because it intentionally lands a touch after its anchor.
  const earliest = Date.now() + MIN_SCHEDULE_DAYS * MS_PER_DAY;
  const latest = Date.now() + MAX_SCHEDULE_DAYS * MS_PER_DAY;
  const ceiling = trigger === 'prop_lock' ? Math.min(t, latest) : latest;
  return new Date(Math.min(Math.max(fuzzed, earliest), ceiling)).toISOString();
}

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function normalizeTrigger(value: string | null | undefined): NotificationTrigger | null {
  switch (value) {
    case 'prop_lock':
    case 'prop_locks':
    case 'PROP_LOCK':
      return 'prop_lock';
    case 'prize_win':
    case 'prize_won':
    case 'PRIZE_WIN':
      return 'prize_win';
    case 'draft_start':
    case 'DRAFT_START':
      return 'draft_start';
    case 'game_start':
    case 'game_started':
    case 'GAME_START':
      return 'game_start';
    case 'settlement':
    case 'settled':
    case 'SETTLEMENT':
      return 'settlement';
    case 'prize_claim_reminder':
      return 'prize_claim_reminder';
    case 'daily_pick':
    case 'DAILY_PICK':
      return 'daily_pick';
    case 'daily_score':
    case 'DAILY_SCORE':
      return 'daily_score';
    case 'picks_reminder':
    case 'PICKS_REMINDER':
      return 'picks_reminder';
    default:
      return null;
  }
}

function toIso(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
}

function readDate(value: unknown): string | null {
  if (value instanceof Date) return value.toISOString();
  if (typeof value !== 'string') return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readBoolean(value: unknown): boolean {
  return value === true;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function makeDedupeKey(trigger: NotificationTrigger, entityId: string): string {
  return `fantasyprime:notification:${trigger}:${entityId}`;
}

function hasScheduled(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markScheduled(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    // Non-critical: scheduling still succeeded, only local dedupe failed.
  }
}

// Forget a previously-scheduled dedupe entry so a re-render that detects
// new state (e.g. picks were made → cancel prop_lock) doesn't accidentally
// re-schedule the same reminder if state flips back.
function forgetScheduled(trigger: NotificationTrigger, entityId: string): void {
  try {
    localStorage.removeItem(makeDedupeKey(trigger, entityId));
  } catch {
    // ignore
  }
}
